/** @jsxImportSource @emotion/react */
import React from 'react';
import ReactDOM from 'react-dom';
import { AnimatePresence } from 'framer-motion';
import { ModalOverlay, ModalContent, NoteArea, NotesHeader, ButtonsWrapper, CancelButton, SaveButton } from './NoteModal.styled';
import { useKeyboardHeight } from '../../../hooks/use-keyboard-height';

type NoteModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSaveNote?: (content: string) => Promise<void>;
};

export const NoteModal: React.FC<NoteModalProps> = ({ isOpen, onClose, onSaveNote }) => {
  const [noteContent, setNoteContent] = React.useState('');
  const [isSaving, setIsSaving] = React.useState(false);
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
  const { height: keyboardBottom, open: isKeyboardOpen } = useKeyboardHeight();

  const handleSave = React.useCallback(async () => {
    if (onSaveNote && noteContent.trim() && !isSaving) {
      setIsSaving(true);
      try {
        await onSaveNote(noteContent.trim());
        setNoteContent('');
        onClose();
      } catch (error) {
        console.error('Failed to save note:', error);
      } finally {
        setIsSaving(false);
      }
    }
  }, [onSaveNote, noteContent, onClose, isSaving]);

  // Block background touch-scroll, but allow scroll inside the modal content
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    if (!isOpen) return;

    const preventScroll = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      if (contentRef.current?.contains(target)) return;
      e.preventDefault();
    };
    document.addEventListener('touchmove', preventScroll, { passive: false });

    return () => {
      document.removeEventListener('touchmove', preventScroll);
    };
  }, [isOpen]);

  const handleOverlayClick = React.useCallback(() => {
    if (isKeyboardOpen) {
      textareaRef.current?.blur();
    } else {
      onClose();
    }
  }, [isKeyboardOpen, onClose]);

  // Portal to document.body so position:fixed isn't broken by
  // Framer Motion transforms on ancestor motion.divs.
  return ReactDOM.createPortal(
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          keyboardBottom={keyboardBottom}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleOverlayClick}
        >
          <ModalContent
            ref={contentRef}
            className="note-modal-content"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            style={{ touchAction: 'pan-y' }}
          >
            <NotesHeader>
                Add to My Notes
            </NotesHeader>
                <NoteArea
                  ref={textareaRef}
                  placeholder='Start typing a thought you want to remember.'
                  value={noteContent}
                  onChange={(e) => setNoteContent(e.target.value)}
                  disabled={isSaving}
                />
                <ButtonsWrapper>
                  <CancelButton onClick={onClose} disabled={isSaving}>Cancel</CancelButton>
                  <SaveButton onClick={handleSave} disabled={isSaving}>
                    {isSaving ? 'Saving...' : 'Save'}
                  </SaveButton>
                </ButtonsWrapper>
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>,
    document.body,
  );
};